import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API, withCompany } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, ArrowLeft, FileDown, Pencil, Plus, Trash2 } from "lucide-react";
import { ContractForm } from "@/components/contracts/ContractForm";
import { PaymentForm } from "@/components/contracts/PaymentForm";
import { PaymentReport } from "@/components/contracts/PaymentReport";
import { fmtTL, fmtNum, fmtDate } from "@/lib/utils";
import { plabel, LOCKED } from "@/lib/contracts";
import { Warnings } from "@/components/contracts/Warnings";
import { ApproversDialog } from "@/components/contracts/Approvers";
import { ApprovalFlow, StatusBadge } from "@/components/contracts/ApprovalFlow";

const COLS = ["C", "E", "G", "N", "O", "P", "R"];
const COL_LABEL = { C: "Kümülatif", E: "Bu Hak. (KDV Hariç)", G: "KDV Dahil", N: "Kesintiler", O: "Ödenecek", P: "Fatura Kes.", R: "Net" };

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(false);
  const [pay, setPay] = useState(null);
  const [viewId, setViewId] = useState(null);
  const load = () => api.get(`/contracts/${id}`).then(r => setC(r.data)).catch(() => navigate("/sozlesmeler"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!c) return null;

  const excel = (pid) => { window.location.href = withCompany(`${API}/contracts/${id}/report.xlsx${pid ? `?payment_id=${pid}` : ""}`); };
  const removePay = async (p) => {
    if (!window.confirm(`${plabel(p)} hakediş silinsin mi?`)) return;
    try { await api.delete(`/contract-payments/${p.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };
  const removeContract = async () => {
    if (!window.confirm("Sözleşme ve tüm hakedişleri silinsin mi?")) return;
    try { await api.delete(`/contracts/${id}`); toast.success("Sözleşme silindi"); navigate("/sozlesmeler"); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };
  const last = c.payments.at(-1);
  const view = c.payments.find(p => p.id === viewId);

  return (
    <div data-testid="contract-detail">
      <button onClick={() => navigate("/sozlesmeler")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3" data-testid="contract-back"><ArrowLeft className="w-4 h-4" /> Sözleşmeler</button>
      <div className="flex flex-wrap items-start gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono text-muted-foreground">{c.contract_no} · {c.contract_type}</div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl leading-tight" data-testid="contract-firm">{c.firm_name}</h1>
          <div className="text-sm text-muted-foreground">{c.subject} — {c.project_name}</div>
        </div>
        <ApproversDialog />
        <Button variant="outline" onClick={() => setEdit(true)} data-testid="contract-edit"><Pencil className="w-4 h-4 mr-1" /> Düzenle</Button>
        <Button variant="outline" onClick={() => excel()} className="text-emerald-700 border-emerald-600/40" data-testid="contract-excel"><FileDown className="w-4 h-4 mr-1" /> Excel Rapor</Button>
        <Button onClick={() => setPay({})} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" data-testid="new-contract-payment"><Plus className="w-4 h-4 mr-1" /> Yeni Hakediş</Button>
        <Button variant="ghost" size="icon" onClick={removeContract} data-testid="contract-delete"><Trash2 className="w-4 h-4 text-rose-500" /></Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <Stat label="Sözleşme Bedeli" value={fmtTL(c.amount)} />
        <Stat label="Kümülatif Hakediş" value={fmtTL(last?.calc.C || 0)} testid="stat-cumulative" />
        <Stat label="Verilen Avans" value={fmtTL(c.advance_total)} />
        <Stat label="Kalan Avans" value={fmtTL(last ? last.advance_remaining : c.advance_total)} testid="stat-adv-remaining" />
        <Stat label="Son Net Ödenecek" value={fmtTL(last?.calc.R || 0)} accent testid="stat-last-net" />
      </div>
      {last && <div className="mb-6"><Warnings items={last.warnings} /></div>}
      <Tabs defaultValue="payments">
        <TabsList><TabsTrigger value="payments" data-testid="tab-payments">Ödeme Takip</TabsTrigger><TabsTrigger value="green" data-testid="tab-green">Yeşil Defter</TabsTrigger><TabsTrigger value="advance" data-testid="tab-advance">Avans Takip</TabsTrigger></TabsList>
        <TabsContent value="payments">
          <Table heads={["No", "Tarih", "Durum", ...COLS.map(k => `${k} ${COL_LABEL[k]}`), ""]} testid="payments-table">
            {c.payments.map(p => (
              <tr key={p.id} className="border-t border-border hover:bg-muted/40 cursor-pointer" onClick={() => setViewId(p.id)} data-testid={`cp-row-${p.no}`}>
                <td className="p-2 font-semibold">{p.label || p.no}{p.warnings?.length > 0 && <AlertTriangle className="inline w-3.5 h-3.5 ml-1 text-rose-600" data-testid={`cp-warn-${p.no}`} />}</td><td className="p-2">{fmtDate(p.date)}</td>
                <td className="p-2"><StatusBadge status={p.status} /></td>
                {COLS.map(k => <td key={k} className={`p-2 text-right font-mono-nums ${k === "R" ? "font-bold" : ""} ${p.calc[k] < 0 ? "text-rose-600" : ""}`}>{fmtTL(p.calc[k])}</td>)}
                <td className="p-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  <Button size="icon" variant="ghost" disabled={LOCKED.includes(p.status)} title={LOCKED.includes(p.status) ? "Onaylı hakediş kilitli" : ""} onClick={() => setPay(p)} data-testid={`cp-edit-${p.no}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" disabled={LOCKED.includes(p.status)} onClick={() => removePay(p)} data-testid={`cp-delete-${p.no}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                </td>
              </tr>
            ))}
          </Table>
        </TabsContent>
        <TabsContent value="green">
          <Table heads={["İmalat", "Birim", "Birim Fiyat", "Önceki Küm.", "Bu Hakediş", "Kümülatif", "Sözleşme Miktarı", "İlerleme", "Kümülatif Tutar"]} testid="green-table">
            {c.green_book.map(g => (
              <tr key={g.id} className="border-t border-border">
                <td className="p-2">{g.name}</td><td className="p-2">{g.unit}</td>
                {[g.unit_price, g.prev_qty, g.this_qty, g.cum_qty, g.contract_qty].map((v, i) => <td key={i} className="p-2 text-right font-mono-nums">{fmtNum(v)}</td>)}
                <td className={`p-2 text-right font-semibold ${g.progress > 100 ? "text-rose-600" : ""}`}>{g.progress != null ? `%${g.progress}` : "-"}</td>
                <td className="p-2 text-right font-mono-nums">{fmtTL(g.cum_amount)}</td>
              </tr>
            ))}
          </Table>
        </TabsContent>
        <TabsContent value="advance">
          <div className="grid md:grid-cols-2 gap-4">
            <Table heads={["Avans / Çek", "Tutar"]} testid="advances-table">
              {c.advances.map(a => <tr key={a.id} className="border-t border-border"><td className="p-2">{a.description}</td><td className="p-2 text-right font-mono-nums">{fmtTL(a.amount)}</td></tr>)}
            </Table>
            <Table heads={["Hakediş", "Avans Kesintisi", "Kalan Avans"]} testid="advance-cut-table">
              {c.payments.map(p => <tr key={p.id} className="border-t border-border"><td className="p-2">{plabel(p)}</td><td className="p-2 text-right font-mono-nums">{fmtTL(p.calc.K)}</td><td className="p-2 text-right font-mono-nums">{fmtTL(p.advance_remaining)}</td></tr>)}
            </Table>
          </div>
        </TabsContent>
      </Tabs>
      <ContractForm open={edit} onOpenChange={setEdit} initial={c} onSaved={load} />
      {pay && <PaymentForm open={!!pay} onOpenChange={(o) => !o && setPay(null)} contract={c} payment={pay.id ? pay : null} onSaved={load} />}
      <Dialog open={!!view} onOpenChange={(o) => !o && setViewId(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payment-view">
          <DialogHeader><DialogTitle>{plabel(view)} Hakediş Raporu · {fmtDate(view?.date)}</DialogTitle></DialogHeader>
          {view && <ApprovalFlow payment={view} onChanged={load} />}
          {view && <Warnings items={view.warnings} testid="view-warnings" />}
          {view && <PaymentReport calc={view.calc} />}
          <Button variant="outline" onClick={() => excel(view.id)} data-testid="payment-view-excel"><FileDown className="w-4 h-4 mr-1" /> Bu Hakedişi Excel'e Aktar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? "border-amber-500/40 bg-amber-500/10" : "border-border bg-card"}`} data-testid={testid}>
      <div className="text-xs text-muted-foreground">{label}</div><div className="font-mono-nums font-bold mt-1">{value}</div>
    </div>
  );
}

function Table({ heads, children, testid }) {
  return (
    <div className="rounded-xl border border-border overflow-x-auto mt-3">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-muted/50 text-xs text-muted-foreground"><tr>{heads.map((h, i) => <th key={i} className="p-2 text-left whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
